import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import QuestionCard from './QuestionCard';
import { OptionPercentageStat } from './OptionPercentageStat';
import { CircleAddIcon } from './icons/CircleAddIcon';
import { EditIcon } from './icons/EditIcon';
import { PlayerSilhouetteIcon } from './icons/PlayerSilhouetteIcon';
import { LockBadge } from './shared/LockBadge';
import {
  getPickLockedBackgroundClass,
  getPickLockedBorderClass,
  getPickLockedLabelClass,
  getPickLockedSecondaryClass,
} from './shared/pickLockedStyles';
import {
  getPickOptionTextClass,
  getPickResultSurfaceClass,
  hasPickResult,
} from './shared/pickResultStyles';

/**
 * LeaderPick — cross-fixture leader question (DKPLAT-354).
 *
 * Figma CD:
 *   Card empty: 10203:61286
 *   Card selected: 10203:61855
 *   Locked selected: 10203:56120
 *   Correct: 10203:57379
 *   Drawer: 10203:61737
 *   Drawer selected row: 10203:62047
 */

export interface LeaderCandidate {
  /** Option ID — what gets stored as the user's selection */
  id: string;
  /** Display name (e.g. "Patrick Mahomes" or "Kansas City Chiefs") */
  name: string;
  /** Optional player position (e.g. "QB") — only for player_leader */
  position?: string;
  /** Team trigram (e.g. "KC") shown next to the name */
  teamShortcode?: string;
  /** Headshot / team logo URL */
  imageUrl?: string;
  /** Small team logo to show alongside the headshot */
  teamLogoUrl?: string;
  /** Fixture label, e.g. "ATL @ CIN" — useful when candidates span multiple games */
  fixtureLabel?: string;
  /** Matchup time, e.g. "Sun, 1:00 PM" */
  matchTime?: string;
  /** Current accumulated stat value across the contest window */
  statValue: number;
  /** Popularity percentage — share of other users who picked this option */
  popularityPct?: number;
}

export interface LeaderPickProps {
  /** Candidate list, in the order they should appear (already ranked by stat) */
  options: LeaderCandidate[];
  /** Stat label, e.g. "Passing Yards", shown in both card + modal */
  statLabel: string;
  /** Kind of leader question — controls placeholder copy + modal title */
  kind: 'player' | 'team';
  /** Currently selected option id, if any */
  selectedOptionId?: string;
  /** Called when the user picks an option in the modal */
  onSelect: (optionId: string | undefined) => void;
  /** How many options to show before "Show all" expands the list */
  defaultLimit?: number;
  /** Question metadata for QuestionCard styling */
  points: number;
  earnedPoints?: number;
  questionText?: string;
  subtext?: string;
  isCorrect?: boolean | null;
  disabled?: boolean;
  locked?: boolean;
  questionId?: string;
  questionNumber?: number;
  questionMetadata?: { dailyDouble?: boolean };
}

// Generic team-shield silhouette — used when a candidate is a team but the
// feed didn't return a logo URL.
const PLACEHOLDER_TEAM =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none"><path d="M32 6 L54 14 V32 C54 44 44 54 32 58 C20 54 10 44 10 32 V14 Z" stroke="#6F6F6F" stroke-width="3" stroke-linejoin="round"/></svg>`
  );

const LeaderPick: React.FC<LeaderPickProps> = ({
  options,
  statLabel,
  kind,
  selectedOptionId,
  onSelect,
  defaultLimit = 30,
  points,
  earnedPoints,
  questionText,
  subtext,
  isCorrect,
  disabled = false,
  locked = false,
  questionId,
  questionNumber,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslation();

  // Daily Double chrome is unused for Circus Daily.
  const isDailyDouble = false;

  useEffect(() => {
    if (!isModalOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isModalOpen]);

  const selected = useMemo(
    () => options.find(o => o.id === selectedOptionId),
    [options, selectedOptionId]
  );

  const visibleOptions = showAll ? options : options.slice(0, defaultLimit);

  const promptLine = subtext || `Select a ${kind} to have...`;
  const titleLine = questionText || statLabel;
  const selectLabel = kind === 'team' ? t('question.selectTeam') : t('question.selectPlayer');
  const modalTitle = kind === 'team' ? t('question.pick1Team') : t('question.pick1Player');
  const columnLabel = kind === 'team' ? t('leaderboard.user') : t('leaderboard.player');

  const hasResult = hasPickResult(isCorrect);
  const resultSurfaceClass = getPickResultSurfaceClass(!!selected, isCorrect);
  const showLockBadge = locked && !!selected && !hasResult;

  const getCardBackgroundClass = () => {
    if (resultSurfaceClass) return '';
    if (locked) return getPickLockedBackgroundClass(!!selected);
    if (selected) return 'bg-white';
    return 'bg-surface-muted';
  };

  const getCardBorderClass = () => {
    if (resultSurfaceClass) return resultSurfaceClass;
    if (locked) return getPickLockedBorderClass(!!selected);
    if (selected) return 'border border-transparent';
    return 'border border-transparent';
  };

  const labelClass =
    locked && selected && !hasResult
      ? getPickLockedLabelClass()
      : getPickOptionTextClass(!!selected, hasResult);
  const secondaryClass =
    locked && selected && !hasResult
      ? getPickLockedSecondaryClass()
      : selected && !hasResult
        ? 'text-surface-muted'
        : 'text-muted-foreground';

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      questionNumber={questionNumber}
      isDailyDouble={isDailyDouble}
      isCorrect={isCorrect}
      data-testid={questionId ? `question-${questionId}` : 'leader-pick'}
      data-testtype="leader"
    >
      {/* Question — Figma: muted prompt + white title */}
      <div className="flex flex-col gap-1 items-center text-center w-full">
        <span
          data-testid="leader-subtext"
          className="text-muted-foreground font-sans text-body-sm w-full"
        >
          {promptLine}
        </span>
        <span
          data-testid="leader-question"
          className="text-white font-sans text-body-default w-full"
        >
          {titleLine}
        </span>
      </div>

      {/* Collapsed selection card — empty / selected / locked / correct / incorrect */}
      <button
        type="button"
        onClick={() => !disabled && !locked && !hasResult && setIsModalOpen(true)}
        disabled={disabled || locked || hasResult}
        data-testid="leader-open-modal"
        data-selected={selected ? 'true' : 'false'}
        data-locked={locked ? 'true' : undefined}
        data-testresult={
          selected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined
        }
        className={`relative flex flex-col w-full h-[160px] rounded-xl overflow-visible transition-smooth ${getCardBackgroundClass()} ${getCardBorderClass()} ${
          disabled || locked || hasResult
            ? 'cursor-not-allowed'
            : selected
              ? ''
              : 'hover:brightness-110'
        }`}
      >
        {/* Image area */}
        <div
          className={`relative flex-1 min-h-0 w-full rounded-t-xl ${
            hasResult || locked ? '' : selected ? 'bg-white' : ''
          }`}
        >
          {selected ? (
            <>
              {selected.popularityPct !== undefined && (
                <div className="absolute left-1.5 top-1.5 z-10 w-7">
                  <OptionPercentageStat
                    percentage={selected.popularityPct}
                    isSelected={!!selected && !hasResult && !locked}
                  />
                </div>
              )}
              {selected.teamLogoUrl && (
                <img
                  src={selected.teamLogoUrl}
                  alt=""
                  className="absolute right-1 top-1.5 size-10 object-contain z-10"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <div className="absolute inset-x-0 bottom-0 flex justify-center h-[115px] overflow-hidden">
                {selected.imageUrl || kind === 'team' ? (
                  <img
                    src={selected.imageUrl || PLACEHOLDER_TEAM}
                    alt={selected.name}
                    className="h-full w-auto max-w-[140px] object-contain object-bottom"
                    onError={e => {
                      if (kind === 'team') {
                        e.currentTarget.src = PLACEHOLDER_TEAM;
                      } else {
                        e.currentTarget.style.display = 'none';
                      }
                    }}
                  />
                ) : (
                  <PlayerSilhouetteIcon
                    className={`h-[115px] w-[140px] ${
                      hasResult || locked ? 'text-muted-foreground' : 'text-background'
                    }`}
                  />
                )}
                {showLockBadge && (
                  <LockBadge
                    data-testid="lock-badge-leader"
                    className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2"
                  />
                )}
              </div>
              {/* Edit CTA — hidden when locked or graded */}
              {!locked && !hasResult && (
                <span className="absolute right-1.5 bottom-1.5 z-10 inline-flex items-center justify-center gap-0.5 rounded border border-background bg-white pl-1.5 pr-2 py-1.5 pointer-events-none">
                  <EditIcon className="size-2.5 text-background" />
                  <span className="font-sans text-label-sm-bold capitalize text-background whitespace-nowrap">
                    Edit
                  </span>
                </span>
              )}
            </>
          ) : (
            <div className="absolute inset-x-0 bottom-0 flex justify-center h-[115px] overflow-hidden pointer-events-none">
              {kind === 'team' ? (
                <img
                  src={PLACEHOLDER_TEAM}
                  alt=""
                  className="h-[80px] w-auto object-contain opacity-70 self-center"
                />
              ) : (
                <PlayerSilhouetteIcon className="h-[115px] w-[140px] text-background" />
              )}
            </div>
          )}

          {!selected && !locked && !hasResult && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <CircleAddIcon className="size-6 text-white" />
            </div>
          )}
        </div>

        {/* Divider + label */}
        <div
          className={`border-t-2 flex flex-col gap-1 items-center justify-center px-2 py-3 shrink-0 w-full rounded-b-xl ${
            hasResult || locked
              ? 'border-white/20 bg-transparent'
              : selected
                ? 'border-neutral-200 bg-white'
                : 'border-surface bg-surface-muted py-4'
          }`}
        >
          {selected ? (
            <>
              <p
                className={`font-sans text-label-bold capitalize truncate w-full text-center whitespace-nowrap ${labelClass}`}
              >
                {selected.name}
              </p>
              {(selected.position || selected.teamShortcode) && (
                <p className={`font-sans text-label capitalize w-full text-center ${secondaryClass}`}>
                  {selected.position || selected.teamShortcode}
                </p>
              )}
            </>
          ) : (
            <p className="text-white font-sans text-label-bold capitalize text-center w-full">
              {selectLabel}
            </p>
          )}
        </div>
      </button>

      {/* Selection drawer — Figma 10203:61737 */}
      {isModalOpen && !locked && !hasResult && (
        <div
          data-testid="leader-modal"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="relative flex w-full max-w-lg max-h-[85vh] flex-col gap-5 border-t-2 border-border bg-surface pt-8 pb-0 shadow-[0_4px_16px_rgba(18,18,18,0.1)] rounded-t-none"
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              aria-label="Close"
              className="absolute right-3 top-2.5 text-white opacity-80 hover:opacity-100"
            >
              <X className="size-6" strokeWidth={1.5} />
            </button>

            <div className="flex flex-col items-center gap-3 px-0 text-center">
              <h2 className="w-full font-sans text-header-01 text-white">{modalTitle}</h2>
            </div>

            <div className="flex flex-col gap-5 items-stretch overflow-hidden w-full min-h-0 flex-1">
              <div className="flex flex-col gap-1 items-center text-center w-full px-0">
                <p className="text-muted-foreground font-sans text-body-sm w-full">{promptLine}</p>
                <p className="text-white font-sans text-body-default w-full">{titleLine}</p>
              </div>

              <div className="flex flex-col gap-3 items-stretch w-full min-h-0 flex-1 overflow-hidden">
                {/* Table header */}
                <div className="flex items-center gap-2 px-3 text-white font-sans text-label-sm capitalize">
                  <span className="flex-1">{columnLabel}</span>
                  <span className="w-20 text-center shrink-0">Stat Avg</span>
                </div>

                {/* Table body */}
                <div className="flex-1 overflow-y-auto border-t border-border min-h-0">
                  {visibleOptions.map(opt => {
                    const isPicked = opt.id === selectedOptionId;
                    const nameClass = isPicked ? 'text-background' : 'text-white';
                    const secondaryClass = isPicked ? 'text-surface-muted' : 'text-muted-foreground';
                    return (
                      <button
                        type="button"
                        key={opt.id}
                        data-testid={`leader-option-${opt.id}`}
                        data-selected={isPicked ? 'true' : 'false'}
                        onClick={() => {
                          onSelect(isPicked ? undefined : opt.id);
                          setIsModalOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-smooth border-b ${
                          isPicked
                            ? 'bg-white border-neutral-200 border-t'
                            : 'bg-surface border-border hover:brightness-110'
                        }`}
                      >
                        <div className="flex flex-1 items-center gap-3 min-w-0 h-12">
                          <div
                            className={`size-12 shrink-0 rounded-full overflow-hidden flex items-end justify-center ${
                              isPicked ? 'bg-neutral-100' : 'bg-[#121212]'
                            }`}
                          >
                            {opt.imageUrl || kind === 'team' ? (
                              <img
                                src={opt.imageUrl || PLACEHOLDER_TEAM}
                                alt={opt.name}
                                className="size-full object-cover object-top"
                                onError={e => {
                                  if (kind === 'team') {
                                    e.currentTarget.src = PLACEHOLDER_TEAM;
                                  } else {
                                    e.currentTarget.style.display = 'none';
                                  }
                                }}
                              />
                            ) : (
                              <PlayerSilhouetteIcon className="w-[53px] h-11 text-background" />
                            )}
                          </div>
                          <div className="flex flex-col gap-0.5 items-start min-w-0 flex-1">
                            <p
                              className={`font-sans text-label-bold capitalize truncate w-full whitespace-nowrap ${nameClass}`}
                            >
                              {opt.name}
                            </p>
                            <div className="flex items-center gap-1 w-full min-w-0">
                              {(opt.position || opt.teamShortcode) && (
                                <p
                                  className={`font-sans text-label-sm capitalize truncate ${secondaryClass}`}
                                >
                                  {opt.position || opt.teamShortcode}
                                </p>
                              )}
                              {opt.teamLogoUrl && (
                                <img
                                  src={opt.teamLogoUrl}
                                  alt=""
                                  className="size-4 object-contain shrink-0"
                                  onError={e => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              )}
                            </div>
                            {(opt.fixtureLabel || opt.matchTime) && (
                              <p
                                className={`font-sans text-label-sm truncate w-full ${secondaryClass}`}
                              >
                                {[opt.fixtureLabel, opt.matchTime].filter(Boolean).join('  ')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div
                          className={`w-20 shrink-0 text-center font-sans text-label-bold tabular-nums capitalize ${nameClass}`}
                        >
                          {opt.statValue.toLocaleString()}
                        </div>
                      </button>
                    );
                  })}
                  {!showAll && options.length > defaultLimit && (
                    <button
                      type="button"
                      onClick={() => setShowAll(true)}
                      data-testid="leader-show-all"
                      className="w-full text-center text-muted-foreground font-sans text-label py-3 hover:text-white"
                    >
                      Show all ({options.length})
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </QuestionCard>
  );
};

export default LeaderPick;
